import {
  Box,
  Button,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  Toolbar,
  InputAdornment,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  TableSortLabel,
} from '@mui/material';
import { useMemo, useState } from 'react';

import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import { useGetAllPropertyManagers } from '@/api/endpoints/propertyManager/useGetAllPropertyManagers';
import { PropertyManagementWithManagerWithLimitedUserView } from '@/api/models/propertyManagement';
import { ChevronLeftIcon } from '@/assets/icons/ChevronLeftIcon';
import { ChevronRightIcon } from '@/assets/icons/ChevronRightIcon';
import { DotsVerticalIcon } from '@/assets/icons/DotsVerticalIcon';
import { EditIcon } from '@/assets/icons/EditIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { SearchMdIcon } from '@/assets/icons/SearchMdIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { useAuth } from '@/context/AuthProvider';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { formatPhoneNumber } from '@/utils/phone';

import { ConfirmActionModal } from '@/components/confirm/ConfirmActionModal';
import PropertyManagerDeleteModal from '@/components/propertyManager/PropertyManagerDeleteModal';
import PropertyManagerUpdateModal from '@/components/propertyManager/PropertyManagerUpdateModal';

import InviteHouseholdMemberModal from './InviteHouseholdMemberModal';

interface PropertyManagerMenuState {
  anchorEl: HTMLElement | null;
  propertyManager: PropertyManagementWithManagerWithLimitedUserView | null;
}

type Order = 'asc' | 'desc';
type PropertyManager = PropertyManagementWithManagerWithLimitedUserView;
type SortableField = 'full_name' | 'role';

function descendingComparator(a: PropertyManager, b: PropertyManager, orderBy: SortableField) {
  if (orderBy === 'full_name') {
    const aValue = String(a.manager.limitedUser.full_name ?? '').toLowerCase();
    const bValue = String(b.manager.limitedUser.full_name ?? '').toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  if (orderBy === 'role') {
    const aValue = String(a.role ?? '').toLowerCase();
    const bValue = String(b.role ?? '').toLowerCase();
    if (bValue < aValue) return -1;
    if (bValue > aValue) return 1;
    return 0;
  }
  return 0;
}

function getComparator(order: Order, orderBy: SortableField) {
  return order === 'desc'
    ? (a: PropertyManager, b: PropertyManager) => descendingComparator(a, b, orderBy)
    : (a: PropertyManager, b: PropertyManager) => -descendingComparator(a, b, orderBy);
}

const InviteHouseholdMemberPage: React.FC = () => {
  const { user } = useAuth();
  const { selectedPropertyLocation } = usePropertyManager();
  const theme = useTheme();

  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const [isPropertyManagerUpdateModalOpen, setIsPropertyManagerUpdateModalOpen] = useState(false);
  const [isPropertyManagerDeleteModalOpen, setIsPropertyManagerDeleteModalOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  const [propertyManagerToUpdate, setPropertyManagerToUpdate] =
    useState<PropertyManagementWithManagerWithLimitedUserView | null>(null);
  const [propertyManagerToDelete, setPropertyManagerToDelete] =
    useState<PropertyManagementWithManagerWithLimitedUserView | null>(null);

  const [menuState, setMenuState] = useState<PropertyManagerMenuState>({
    anchorEl: null,
    propertyManager: null,
  });

  const [filterName, setFilterName] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<SortableField>('full_name');

  const getAllPropertyManagersQuery = useGetAllPropertyManagers({
    location_id: selectedPropertyLocation?.id,
  });

  const handleChangeRowsPerPage = (event: SelectChangeEvent<number>, _: React.ReactNode) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const handleFilterName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFilterName(event.target.value);
    setPage(0);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const filteredData = useMemo(() => {
    let filtered = getAllPropertyManagersQuery.data || [];

    // Apply name filter
    if (filterName) {
      filtered = filtered.filter(
        (propertyManager: PropertyManager) =>
          propertyManager.manager.limitedUser.full_name
            ?.toLowerCase()
            .includes(filterName.toLowerCase()) ?? false
      );
    }

    // Sort the data
    return [...filtered].sort(getComparator(order, orderBy));
  }, [getAllPropertyManagersQuery.data, filterName, order, orderBy]);

  const handleSort = (property: SortableField) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    propertyManager: PropertyManagementWithManagerWithLimitedUserView
  ) => {
    event.stopPropagation();
    setMenuState({
      anchorEl: event.currentTarget,
      propertyManager,
    });
  };

  const handleMenuClose = () => {
    setMenuState({
      anchorEl: null,
      propertyManager: null,
    });
  };

  const handleEditPropertyManager = () => {
    if (menuState.propertyManager) {
      setPropertyManagerToUpdate(menuState.propertyManager);
      setIsPropertyManagerUpdateModalOpen(true);
    }
    handleMenuClose();
  };

  const handleDeleteConfirm = () => {
    if (menuState.propertyManager) {
      setPropertyManagerToDelete(menuState.propertyManager);
      setDeleteConfirmOpen(true);
      handleMenuClose();
    }
  };

  const handleDeletePropertyManager = () => {
    if (propertyManagerToDelete) {
      setPropertyManagerToDelete(null);
      setDeleteConfirmOpen(false);
      // The actual delete will be handled by the PropertyManagerDeleteModal
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setPropertyManagerToDelete(null);
  };

  const getAvatarProps = (name: string | undefined) => {
    if (!name) {
      return {
        children: '?',
        sx: {
          bgcolor: 'grey.500',
          color: 'white',
          width: 32,
          height: 32,
          fontSize: '0.875rem',
        },
      };
    }

    const initials = name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('')
      .substring(0, 2);

    // Generate a consistent color based on the name
    const colors = ['primary', 'secondary', 'error', 'warning', 'info', 'success'] as const;
    const colorIndex = name.length % colors.length;

    return {
      children: initials,
      sx: {
        bgcolor: `${colors[colorIndex]}.main`,
        color: 'white',
        width: 32,
        height: 32,
        fontSize: '0.875rem',
      },
    };
  };

  if (!selectedPropertyLocation) {
    return (
      <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
        <Typography variant="body2" color="text.secondary">
          Please select a property location to view users.
        </Typography>
      </Stack>
    );
  }

  const usersCount = filteredData.length;

  return (
    <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
      <DashboardHeader title={`User Management (${usersCount})`}>
        <Button
          color="primary"
          variant="contained"
          size="small"
          startIcon={<PlusIcon />}
          onClick={() => setIsInviteModalOpen(true)}
        >
          Invite User
        </Button>
      </DashboardHeader>

      <Typography
        variant="body1"
        color="text.secondary"
        sx={{
          mb: 2,
          mt: { xs: 1, sm: 0 },
        }}
      >
        Manages users for {selectedPropertyLocation.address_text}
      </Typography>

      <Box sx={{ flex: 1 }}>
        <Toolbar
          sx={{
            height: 64,
            display: 'flex',
            justifyContent: 'space-between',
            p: theme.spacing(2),
            borderBottom: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <OutlinedInput
            fullWidth
            value={filterName}
            onChange={handleFilterName}
            placeholder="Search users..."
            startAdornment={
              <InputAdornment position="start">
                <SearchMdIcon sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
            sx={{
              maxWidth: 320,
              bgcolor: 'common.white',
            }}
          />
        </Toolbar>

        {getAllPropertyManagersQuery.isLoading && (
          <Typography variant="body2" color="text.secondary">
            Loading users...
          </Typography>
        )}

        {getAllPropertyManagersQuery.isError && (
          <Typography variant="body2" color="error">
            Error loading users: {getAllPropertyManagersQuery.error?.message}
          </Typography>
        )}

        {getAllPropertyManagersQuery.data && (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 'none',
              border: '0px solid',
              borderColor: 'divider',
              bgcolor: 'common.white',
            }}
          >
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: theme.palette.secondary[200] }}>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'full_name'}
                      direction={orderBy === 'full_name' ? order : 'asc'}
                      onClick={() => handleSort('full_name')}
                    >
                      Name
                    </TableSortLabel>
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      textTransform: 'none',
                    }}
                  >
                    Email
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      textTransform: 'none',
                    }}
                  >
                    Phone
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 'bold',
                      color: 'text.primary',
                      fontSize: '0.875rem',
                      cursor: 'pointer',
                      textTransform: 'none',
                      '&:hover': { bgcolor: `${theme.palette.secondary[200]} !important` },
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === 'role'}
                      direction={orderBy === 'role' ? order : 'asc'}
                      onClick={() => handleSort('role')}
                    >
                      Role
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ width: 48 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {filterName
                          ? 'No users found matching your search.'
                          : 'No users found. Click "Invite User" to invite your first user.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredData
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(propertyManager => {
                      const isCurrentUser = user?.id === propertyManager.manager.limitedUser.id;
                      const isCurrentUserAdmin = propertyManager.role === 'ADMIN';
                      const shouldHideActions = isCurrentUser && isCurrentUserAdmin;

                      return (
                        <TableRow
                          key={propertyManager.id}
                          sx={{
                            '&:not(:last-child)': {
                              borderBottom: '1px solid',
                              borderColor: 'divider',
                            },
                          }}
                        >
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Avatar
                                {...getAvatarProps(propertyManager.manager.limitedUser.full_name)}
                              />
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography variant="body3" color="text.primary">
                                  {propertyManager.manager.limitedUser.full_name || 'Unnamed User'}
                                </Typography>
                                {propertyManager.manager.is_emergency_contact && (
                                  <Chip
                                    label="Emergency Contact"
                                    color="error"
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Stack>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body3" color="text.primary">
                              {propertyManager.manager.limitedUser.email}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body3" color="text.primary">
                              {propertyManager.manager.limitedUser.phone
                                ? formatPhoneNumber(propertyManager.manager.limitedUser.phone)
                                : '–'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={propertyManager.role}
                              size="small"
                              variant="outlined"
                              color={propertyManager.role === 'ADMIN' ? 'primary' : 'secondary'}
                              sx={{ fontSize: '0.75rem', height: 24 }}
                            />
                          </TableCell>
                          <TableCell>
                            {!shouldHideActions && (
                              <IconButton
                                size="small"
                                onClick={e => handleMenuOpen(e, propertyManager)}
                              >
                                <DotsVerticalIcon />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 2,
            borderTop: '1px solid',
            borderColor: 'divider',
            bgcolor: 'common.white',
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              size="small"
              sx={{ width: 80 }}
              inputProps={{ 'aria-label': 'rows per page' }}
            >
              <MenuItem value={5}>5</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="body2" color="text.secondary">
              {`${page * rowsPerPage + 1}-${Math.min(
                (page + 1) * rowsPerPage,
                filteredData.length
              )} of ${filteredData.length}`}
            </Typography>
            <IconButton
              onClick={() => handleChangePage(null, page - 1)}
              disabled={page === 0}
              size="small"
            >
              <ChevronLeftIcon />
            </IconButton>
            <IconButton
              onClick={() => handleChangePage(null, page + 1)}
              disabled={page >= Math.ceil(filteredData.length / rowsPerPage) - 1}
              size="small"
            >
              <ChevronRightIcon />
            </IconButton>
          </Stack>
        </Box>
      </Box>

      {/* Dropdown Menu */}
      <Menu
        anchorEl={menuState.anchorEl}
        open={Boolean(menuState.anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={handleEditPropertyManager}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteConfirm} sx={{ color: 'error.main' }}>
          <ListItemIcon>
            <Trash01Icon fontSize="small" sx={{ color: 'error.main' }} />
          </ListItemIcon>
          <ListItemText>Remove</ListItemText>
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Modal */}
      {deleteConfirmOpen && propertyManagerToDelete && (
        <ConfirmActionModal
          title="Remove User"
          onConfirm={handleDeletePropertyManager}
          onReject={handleDeleteCancel}
        >
          <Typography>
            Are you sure you want to remove "
            {propertyManagerToDelete.manager.limitedUser.full_name || 'Unnamed User'}"? This action
            cannot be undone.
          </Typography>
        </ConfirmActionModal>
      )}

      {isInviteModalOpen && selectedPropertyLocation && (
        <InviteHouseholdMemberModal
          onClose={() => setIsInviteModalOpen(false)}
          propertyLocationId={selectedPropertyLocation.id}
          onHouseholdMemberCreated={() => {
            getAllPropertyManagersQuery.refetch();
          }}
        />
      )}

      {isPropertyManagerUpdateModalOpen && propertyManagerToUpdate && (
        <PropertyManagerUpdateModal
          propertyManager={propertyManagerToUpdate}
          onClose={() => {
            setPropertyManagerToUpdate(null);
            setIsPropertyManagerUpdateModalOpen(false);
          }}
        />
      )}

      {isPropertyManagerDeleteModalOpen && propertyManagerToDelete && (
        <PropertyManagerDeleteModal
          propertyManager={propertyManagerToDelete}
          onClose={() => {
            setPropertyManagerToDelete(null);
            setIsPropertyManagerDeleteModalOpen(false);
          }}
          onPropertyManagerDeleted={() => {
            getAllPropertyManagersQuery.refetch();
          }}
        />
      )}
    </Stack>
  );
};

export default InviteHouseholdMemberPage;
